/**
 * LE PANNEAU D'ENVOI — refonte du 29/10/2026, maquette validée par Mohamed.
 *
 * Il se tient À CÔTÉ du document, et non plus au-dessus. La première version
 * se dépliait entre la barre et la feuille, qu'elle poussait hors de l'écran :
 * on préparait l'envoi sans plus voir ce qu'on envoyait. L'écran est large, la
 * feuille fait 794 px — la place existe. Sur un écran étroit, le panneau
 * repasse dessous (`styles.css`, media query).
 *
 * IL NE S'IMPRIME PAS. `styles.css` le masque dans `@media print`, comme la
 * barre d'outils. Sans cela il sortirait sur le PDF envoyé au client, avec les
 * adresses cochées dessus.
 *
 * CE COMPOSANT NE DÉCIDE RIEN : les règles sont dans `envoi.ts`, qui se
 * vérifie sans navigateur. Ici on assemble, on appelle, on affiche.
 */

import { useCallback, useEffect, useState } from 'react';

import { creerContact } from './api.js';
import {
  envoyerConfirmation,
  lireDestinataires,
  type DestinatairesProposes,
} from './confirmation.js';
import { enBase64 } from './confirmation_lue.js';
import {
  adresseValide,
  choixPossibles,
  messageApresEnvoi,
  nomDepuisAdresse,
  nomSuggere,
  peutEnvoyer,
  type Choix,
} from './envoi.js';

/**
 * Le chemin de l'annuaire des clients, écrit ici et non importé d'un écran :
 * ce composant ne dépend d'aucun autre composant. Même choix que
 * `rattacherPennylane` dans `api.ts`, qui le pose en toutes lettres pour la
 * même raison.
 */
const ANNUAIRE_CLIENTS = {
  chemin: 'clients',
  cleListe: 'clients',
  cleFiche: 'client',
} as const;

interface Proprietes {
  readonly jeton: () => Promise<string | null>;
  readonly otId: string;
  /** Le numéro de l'ordre, pour suggérer un nom de fichier. */
  readonly numero: string;
  readonly quandFermer: () => void;
  readonly quandAccesPerdu: () => void;
  /** Les destinataires déjà lus, pour l'aperçu : aucun appel à l'API. */
  readonly dejaLu?: DestinatairesProposes;
}

type Etat =
  | { readonly nom: 'chargement' }
  | { readonly nom: 'refus'; readonly message: string }
  | {
      readonly nom: 'prete';
      readonly choix: readonly Choix[];
      readonly clientId: string;
      readonly clientNom: string;
    };

interface Piece {
  readonly nom: string;
  readonly base64: string;
}

/** Ce qu'on propose de garder après un envoi, une fois le message parti. */
interface AGarder {
  readonly adresse: string;
  readonly nom: string;
}

export function BoiteEnvoi({
  jeton,
  otId,
  numero,
  quandFermer,
  quandAccesPerdu,
  dejaLu,
}: Proprietes) {
  const [etat, setEtat] = useState<Etat>({ nom: 'chargement' });
  const [cochees, setCochees] = useState<readonly string[]>([]);
  /** Les adresses tapées à la main : elles ne viennent pas de l'annuaire. */
  const [libres, setLibres] = useState<readonly string[]>([]);
  const [saisie, setSaisie] = useState('');
  const [copie, setCopie] = useState('');
  const [piece, setPiece] = useState<Piece | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [resultat, setResultat] = useState<{
    readonly ton: 'succes' | 'refus';
    readonly texte: string;
  } | null>(null);
  const [aGarder, setAGarder] = useState<AGarder | null>(null);
  const [gardeFaite, setGardeFaite] = useState<string | null>(null);

  const poser = useCallback((proposes: DestinatairesProposes) => {
    const choix = choixPossibles(proposes);
    setEtat({
      nom: 'prete',
      choix,
      clientId: proposes.client.id,
      clientNom: proposes.client.nom,
    });
    setCochees(choix.filter((c) => c.parDefaut).map((c) => c.adresse));
    setCopie(proposes.copie);
  }, []);

  const charger = useCallback(async () => {
    if (dejaLu !== undefined) {
      poser(dejaLu);
      return;
    }
    const j = await jeton();
    if (j === null) {
      quandAccesPerdu();
      return;
    }
    const lu = await lireDestinataires(j, otId);
    if (lu.etat !== 'ok') {
      if (lu.cause === 'acces') {
        quandAccesPerdu();
        return;
      }
      setEtat({
        nom: 'refus',
        message: lu.message ?? 'Les destinataires n’ont pas pu être lus.',
      });
      return;
    }
    poser(lu.valeur);
  }, [jeton, otId, quandAccesPerdu, dejaLu, poser]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const ajouterLaSaisie = () => {
    const adresse = saisie.trim();
    if (!adresseValide(adresse)) return;
    const dejaLa =
      libres.some((a) => a.toLowerCase() === adresse.toLowerCase()) ||
      (etat.nom === 'prete' &&
        etat.choix.some((c) => c.adresse.toLowerCase() === adresse.toLowerCase()));
    if (!dejaLa) {
      setLibres((avant) => [...avant, adresse]);
      setCochees((avant) => [...avant, adresse]);
    }
    setSaisie('');
    setResultat(null);
  };

  const prendreLeFichier = async (fichier: File | null) => {
    setResultat(null);
    if (fichier === null) {
      setPiece(null);
      return;
    }
    // `enBase64` reçoit des octets et rend du texte : c'est ICI qu'on lit le
    // fichier, parce que `File` appartient au navigateur.
    const octets = await fichier.arrayBuffer();
    setPiece({ nom: fichier.name, base64: enBase64(octets) });
  };

  const envoyer = async () => {
    if (piece === null) return;
    setEnCours(true);
    setResultat(null);
    setAGarder(null);
    try {
      const j = await jeton();
      if (j === null) {
        quandAccesPerdu();
        return;
      }
      const fait = await envoyerConfirmation(j, otId, {
        destinataires: cochees,
        copie: [copie.trim()],
        pieceNom: piece.nom,
        pieceBase64: piece.base64,
      });
      if (fait.etat !== 'ok') {
        if (fait.cause === 'acces') {
          quandAccesPerdu();
          return;
        }
        setResultat({
          ton: 'refus',
          texte: fait.message ?? 'L’envoi n’a pas pu être demandé.',
        });
        return;
      }
      setResultat(messageApresEnvoi(fait.valeur));

      // L'ADRESSE TAPÉE À LA MAIN N'EST PAS AJOUTÉE À L'ANNUAIRE DANS VOTRE
      // DOS : on la propose, vous décidez. Décision de Mohamed du 29/10,
      // contre l'ajout automatique — qui remplirait l'annuaire des fautes de
      // frappe et des adresses de passage.
      //
      // On ne propose que la PREMIÈRE, et seulement si elle est partie : en
      // proposer trois d'affilée transformerait un envoi en formulaire.
      if (fait.valeur.parti) {
        const premiere = libres.find((a) => cochees.includes(a));
        if (premiere !== undefined) {
          setAGarder({ adresse: premiere, nom: nomDepuisAdresse(premiere) });
        }
      }
    } finally {
      setEnCours(false);
    }
  };

  const garder = async () => {
    if (aGarder === null || etat.nom !== 'prete') return;
    const j = await jeton();
    if (j === null) {
      quandAccesPerdu();
      return;
    }
    const fait = await creerContact(j, ANNUAIRE_CLIENTS, etat.clientId, {
      nom: aGarder.nom.trim(),
      courriel: aGarder.adresse,
    });
    if (fait.etat !== 'ok') {
      // ON NE MASQUE PAS L'ÉCHEC, mais on ne le confond pas avec celui de
      // l'envoi : le courriel est parti, c'est l'ajout au carnet qui a raté.
      setAGarder(null);
      setGardeFaite(
        `Le contact n’a pas pu être ajouté : ${fait.message ?? 'refus de l’API'}`,
      );
      return;
    }
    setAGarder(null);
    setGardeFaite(`${aGarder.nom.trim()} a été ajouté aux contacts de ${etat.clientNom}.`);
  };

  if (etat.nom !== 'prete') {
    return (
      <div className="conf-envoi">
        <div className="conf-envoi-haut">
          <b>Envoyer au client</b>
          <button className="conf-envoi-fermer" type="button" onClick={quandFermer}>
            ×
          </button>
        </div>
        <div className="conf-envoi-corps">
          <p className={etat.nom === 'refus' ? 'bandeau bandeau-refus' : 'attente'}>
            {etat.nom === 'refus' ? etat.message : 'Chargement des destinataires…'}
          </p>
        </div>
      </div>
    );
  }

  const tousLesChoix: readonly Choix[] = [
    ...etat.choix,
    ...libres.map((adresse) => ({ adresse, libelle: '', parDefaut: false })),
  ];
  const possible = peutEnvoyer({
    cochees,
    pieceNom: piece === null ? null : piece.nom,
    enCours,
    aucuneAdresse: etat.choix.length === 0,
    copieVide: copie.trim() === '',
  });

  return (
    <div className="conf-envoi">
      <div className="conf-envoi-haut">
        <b>Envoyer au client</b>
        <button className="conf-envoi-fermer" type="button" onClick={quandFermer}>
          ×
        </button>
      </div>

      <div className="conf-envoi-corps">
        <div className="conf-envoi-bloc">
          <span className="conf-envoi-titre">Destinataires</span>
          <div className="conf-envoi-liste">
            {tousLesChoix.map((choix) => {
              const libre = choix.libelle === '';
              return (
                <label
                  key={choix.adresse}
                  className={libre ? 'conf-envoi-case libre' : 'conf-envoi-case'}
                >
                  <input
                    type="checkbox"
                    checked={cochees.includes(choix.adresse)}
                    onChange={(evenement) => {
                      setResultat(null);
                      setCochees((avant) =>
                        evenement.target.checked
                          ? [...avant, choix.adresse]
                          : avant.filter((a) => a !== choix.adresse),
                      );
                    }}
                  />
                  <span className="conf-envoi-texte">
                    {/* PAS DE NOM INVENTÉ pour une adresse tapée à la main.
                        La maquette affichait d'abord « Tapée à la main »
                        au-dessus, ce qui se lisait comme le nom d'une
                        personne. Le fond teinté dit déjà qu'elle ne vient pas
                        de l'annuaire. */}
                    <span className={libre ? 'nom adresse' : 'nom'}>
                      {libre ? choix.adresse : choix.libelle}
                    </span>
                    <span className="adresse">
                      {libre ? 'ajoutée pour cet envoi' : choix.adresse}
                    </span>
                  </span>
                  {libre && (
                    <button
                      className="conf-envoi-retirer"
                      type="button"
                      title="Retirer"
                      onClick={(evenement) => {
                        evenement.preventDefault();
                        setLibres((avant) => avant.filter((a) => a !== choix.adresse));
                        setCochees((avant) => avant.filter((a) => a !== choix.adresse));
                      }}
                    >
                      ×
                    </button>
                  )}
                </label>
              );
            })}
          </div>

          {/* LA SAISIE LIBRE. Demande de Mohamed : « taper un destinataire
              librement, pour ne pas être obligé de retourner taper un
              contact ». */}
          <div className="conf-envoi-ajout">
            <input
              className="conf-envoi-champ"
              type="email"
              placeholder="une autre adresse…"
              value={saisie}
              onChange={(evenement) => {
                setSaisie(evenement.target.value);
              }}
              onKeyDown={(evenement) => {
                if (evenement.key === 'Enter') {
                  evenement.preventDefault();
                  ajouterLaSaisie();
                }
              }}
            />
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={!adresseValide(saisie)}
              onClick={ajouterLaSaisie}
            >
              Ajouter
            </button>
          </div>
        </div>

        <div className="conf-envoi-bloc">
          <span className="conf-envoi-titre">En copie</span>
          <input
            className="conf-envoi-champ"
            type="email"
            value={copie}
            onChange={(evenement) => {
              setResultat(null);
              setCopie(evenement.target.value);
            }}
          />
          <span className="conf-envoi-note">
            Pré-rempli, modifiable — mais jamais vide : un message parti de no-reply@
            doit exister quelque part chez TNS.
          </span>
        </div>

        <div className="conf-envoi-bloc">
          <span className="conf-envoi-titre">Le document</span>
          {piece === null ? (
            <>
              {/* LE CHAMP NATIF EST CACHÉ : il affiche « Choose File / No file
                  chosen », en anglais, et le navigateur interdit de le styler.
                  On habille son étiquette. */}
              <label className="conf-envoi-depot" htmlFor="conf-envoi-fichier">
                <span className="bouton">Choisir le fichier PDF</span>
                <span className="conf-envoi-note">
                  Imprimez d’abord la confirmation avec « Enregistrer au format PDF ».
                  Nom suggéré : <code>{nomSuggere(numero)}</code>
                </span>
              </label>
              <input
                id="conf-envoi-fichier"
                className="conf-envoi-fichier"
                type="file"
                accept="application/pdf,.pdf"
                onChange={(evenement) => {
                  void prendreLeFichier(evenement.target.files?.[0] ?? null);
                }}
              />
            </>
          ) : (
            <div className="conf-envoi-piece">
              <span className="conf-envoi-icone">PDF</span>
              <span className="conf-envoi-texte">
                <span className="nom adresse">{piece.nom}</span>
                <span className="adresse">
                  {Math.max(1, Math.round((piece.base64.length * 3) / 4 / 1024))} Ko
                </span>
              </span>
              <button
                className="conf-envoi-changer"
                type="button"
                onClick={() => {
                  setPiece(null);
                  setResultat(null);
                }}
              >
                Changer
              </button>
            </div>
          )}
        </div>

        {resultat !== null && (
          <p
            className={
              resultat.ton === 'succes' ? 'bandeau bandeau-fait' : 'bandeau bandeau-refus'
            }
            role="status"
          >
            {resultat.texte}
          </p>
        )}

        {aGarder !== null && (
          <div className="conf-envoi-garder">
            <span>
              Garder <code>{aGarder.adresse}</code> dans les contacts de{' '}
              {etat.clientNom} ?
            </span>
            {/* LE NOM EST OBLIGATOIRE côté API (contacts.ts). On en propose un,
                deviné de l'adresse, plutôt que de le demander à blanc. */}
            <input
              className="conf-envoi-champ"
              type="text"
              value={aGarder.nom}
              onChange={(evenement) => {
                setAGarder({ adresse: aGarder.adresse, nom: evenement.target.value });
              }}
            />
            <div className="conf-envoi-garder-pied">
              <button
                className="bouton bouton-discret"
                type="button"
                disabled={aGarder.nom.trim() === ''}
                onClick={() => {
                  void garder();
                }}
              >
                Ajouter aux contacts
              </button>
              <button
                className="conf-envoi-non"
                type="button"
                onClick={() => {
                  setAGarder(null);
                }}
              >
                Non, merci
              </button>
            </div>
          </div>
        )}

        {gardeFaite !== null && <span className="conf-envoi-note">{gardeFaite}</span>}
      </div>

      <div className="conf-envoi-pied">
        <button
          className="bouton conf-primaire conf-envoi-partir"
          type="button"
          disabled={!possible.peut}
          onClick={() => {
            void envoyer();
          }}
        >
          {enCours ? 'Envoi…' : 'Envoyer'}
        </button>
        <span className="conf-envoi-motif">
          {possible.peut
            ? 'Le client recevra le document en pièce jointe.'
            : possible.pourquoi}
        </span>
      </div>
    </div>
  );
}
